import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from './CartContext';
import { products } from '../products';
import { FaTrash, FaPlus, FaMinus, FaArrowLeft, FaShoppingBag, FaWeightHanging } from 'react-icons/fa';
import '../css/CartPage.css';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, addToCart } = useContext(CartContext);
  const navigate = useNavigate();
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Filter cart items
  const dealItems = cart.filter(item => item.isDeal);
  const regularItems = cart.filter(item => !item.isDeal && !item.isDealItem);
  const dealProductItems = cart.filter(item => item.isDealItem);

  // Group deal product items by their dealId
  const groupedDealItems = dealProductItems.reduce((acc, item) => {
    if (!acc[item.dealId]) {
      acc[item.dealId] = {
        items: [],
        dealName: item.dealName,
        total: 0,
        originalTotal: 0,
        savings: 0
      };
    }
    acc[item.dealId].items.push(item);
    acc[item.dealId].total += item.dealPrice * item.quantity;
    acc[item.dealId].originalTotal += item.originalPrice * item.quantity;
    acc[item.dealId].savings = acc[item.dealId].originalTotal - acc[item.dealId].total;
    return acc;
  }, {});

  // Calculate totals
  const regularTotal = regularItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const dealTotals = Object.values(groupedDealItems).reduce((sum, deal) => sum + deal.total, 0);
  const originalDealTotals = Object.values(groupedDealItems).reduce((sum, deal) => sum + deal.originalTotal, 0);
  
  const displayTotal = regularTotal + dealTotals;
  const originalTotal = regularTotal + originalDealTotals;
  const totalSavings = originalTotal - displayTotal;

  // Deal requirements configuration
  const dealRequirements = {
    'disposable-1g-deal': { 
      totalQuantity: 2, 
      productsNeeded: 2,
      category: 'Disposable Cart',
      description: 'Select 2 disposable vape products',
      price: 80
    },
    'disposable-3g-deal': { 
      totalQuantity: 4, 
      productsNeeded: 4,
      category: 'Disposable Cart',
      description: 'Select 4 disposable vape products',
      price: 150
    },
    'cartridge-4-deal': { 
      totalQuantity: 4, 
      productsNeeded: 4,
      category: 'Cartridges',
      description: 'Select 4 cartridge products',
      price: 100
    },
    'cartridge-8-deal': { 
      totalQuantity: 8, 
      productsNeeded: 8,
      category: 'Cartridges',
      description: 'Select 8 cartridge products',
      price: 180
    },
    'edible-6-deal': { 
      totalQuantity: 6, 
      productsNeeded: 6,
      category: 'Edibles',
      description: 'Select 6 edible products',
      price: 100
    },
    'flower-edible-deal': {
      totalQuantity: 1, 
      productsNeeded: 1,
      category: 'exotic',
      description: 'Select 1 exotic flower product, free edible comes with this deal',
      price: 180
    },
    'flower-2oz-deal': { 
      totalQuantity: 2, 
      productsNeeded: 2,
      category: 'flower',
      description: 'Select 2 flower products (2oz total)',
      price: 180
    },
    'flower-4oz-deal': { 
      totalQuantity: 4, 
      productsNeeded: 4,
      category: 'flower',
      description: 'Select 4 flower products (4oz total)',
      price: 350
    },
    'preroll-10-deal': { 
      totalQuantity: 10, 
      productsNeeded: 1,
      category: 'Prerolls',
      description: '10 prerolls included',
      price: 80
    },
    'preroll-20-deal': { 
      totalQuantity: 20, 
      productsNeeded: 1,
      category: 'Prerolls',
      description: '20 prerolls included',
      price: 150
    },
  };

  // Get products for each deal type
  const dealProducts = {
    'disposable-1g-deal': products.filter(p => p.category === 'Disposable Cart').slice(0, 14),
    'disposable-3g-deal': products.filter(p => p.category === 'Disposable Cart').slice(0, 14),
    'cartridge-4-deal': products.filter(p => p.category === 'Cartridges').slice(0, 14),
    'cartridge-8-deal': products.filter(p => p.category === 'Cartridges').slice(0, 14),
    'edible-6-deal': products.filter(p => p.category === 'Edibles').slice(0, 14),
    'flower-edible-deal': products.filter(p => p.category === 'exotic').slice(0, 14),
    'flower-2oz-deal': products.filter(p => p.category === 'flower').slice(0, 14),
    'flower-4oz-deal': products.filter(p => p.category === 'flower').slice(0, 14),
    'preroll-10-deal': products.filter(p => p.category === 'Prerolls').slice(0, 2),
    'preroll-20-deal': products.filter(p => p.category === 'Prerolls').slice(0, 2),
  };

  const handleProductSelect = (dealId, product, e) => {
    e.preventDefault();
    e.stopPropagation();

    const dealReq = dealRequirements[dealId];
    if (!dealReq) return;

    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      let newSelection;
      
      if (isSelected) {
        newSelection = prev.filter(p => p.id !== product.id);
      } else {
        if (prev.length < dealReq.productsNeeded) {
          newSelection = [...prev, product];
        } else {
          return prev;
        }
      }
      
      return newSelection;
    });
  };

  const completeDealSelection = (dealId) => {
    const dealItem = cart.find(item => item.id === dealId);
    const dealReq = dealRequirements[dealId];
    
    if (!dealItem || !dealReq || selectedProducts.length !== dealReq.productsNeeded) return;

    // Remove existing deal items for this deal
    const existingDealItems = cart.filter(item => item.dealId === dealId);
    existingDealItems.forEach(item => removeFromCart(item.id));
    
    // Remove the deal placeholder
    removeFromCart(dealId);

    const quantityPerProduct = Math.ceil(dealReq.totalQuantity / selectedProducts.length);

    // Add the new deal items
    selectedProducts.forEach(product => {
      addToCart({
        ...product,
        quantity: quantityPerProduct,
        dealPrice: dealItem.price / dealReq.totalQuantity * quantityPerProduct,
        originalPrice: product.price,
        isDealItem: true,
        dealId: dealId,
        dealName: dealItem.name
      });
    });

    setSelectedDeal(null);
    setSelectedProducts([]);
  };

  const cancelSelection = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDeal(null);
    setSelectedProducts([]);
  };

  // Get weight label for display
  const getWeightLabel = (weight) => {
    const weightLabels = {
      ounce: 'Ounce (28g)',
      half: 'Half (14g)',
      quarter: 'Quarter (7g)',
      eighth: 'Eighth (3.5g)'
    };
    return weightLabels[weight] || weight;
  };

  return (
    <div className="cart-page">
      <div className="cart-header">
        <button onClick={() => navigate(-1)} className="go-back-button">
          <FaArrowLeft />
          <span>Back</span>
        </button>
        <h1>Shopping Cart</h1>
        <div className="cart-count">{cart.length} items</div>
      </div>
      
      {cart.length === 0 ? (
        <div className="empty-cart">
          <div className="empty-cart-icon">
            <FaShoppingBag />
          </div>
          <h2>Your cart is empty</h2>
          <p>Add some products to get started</p>
          <Link to="/products" className="shop-now-btn">
            <FaShoppingBag />
            <span>Shop Products</span>
          </Link>
        </div>
      ) : (
        <div className="cart-container">
          <div className="cart-items-section">
            <h2 className="section-title">Cart Items ({cart.length})</h2>
            
            {/* Regular items */}
            {regularItems.map(item => (
              <div key={item.id} className="cart-item">
                <div className="item-image">
                  <img src={item.image} alt={item.name} />
                </div>
                <div className="item-details">
                  <div className="item-header">
                    <h3 className="item-name">{item.name}</h3>
                    <div className="item-price">${item.price.toFixed(2)}</div>
                  </div>
                  <div className="item-meta">
                    <p className="item-category">{item.category}</p>
                    {item.weight && (
                      <div className="weight-info">
                        <FaWeightHanging className="weight-icon" />
                        <span className="weight-label">{getWeightLabel(item.weight)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="item-controls">
                    <div className="quantity-controls">
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        className="quantity-btn"
                      >
                        <FaMinus />
                      </button>
                      <span className="quantity-display">{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="quantity-btn"
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <button 
                      onClick={() => removeFromCart(item.id)}
                      className="remove-btn"
                    >
                      <FaTrash />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Deal items */}
            {dealItems.map(deal => {
              const dealReq = dealRequirements[deal.id];
              if (!dealReq) return null;
              
              return (
                <React.Fragment key={deal.id}>
                  {/* Deal placeholder */}
                  <div className="cart-item deal-item">
                    <div className="deal-badge">Hot Deal</div>
                    <div className="deal-header">
                      <h3 className="item-name">{deal.name}</h3>
                      <div className="deal-price">${deal.price.toFixed(2)}</div>
                      <p className="deal-description">{dealReq.description}</p>
                    </div>
                    
                    <div className="deal-controls">
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedDeal(deal.id);
                          setSelectedProducts([]);
                        }}
                        className="select-products-btn"
                      >
                        {groupedDealItems[deal.id] 
                          ? 'Change Selection' 
                          : 'Select Products'}
                      </button>

                      <button 
                        onClick={() => {
                          removeFromCart(deal.id);
                          const itemsToRemove = cart.filter(item => item.dealId === deal.id);
                          itemsToRemove.forEach(item => removeFromCart(item.id));
                        }}
                        className="remove-deal-btn"
                      >
                        <FaTrash />
                        <span>Remove Deal</span>
                      </button>
                    </div>
                  </div>

                  {/* Associated deal products */}
                  {groupedDealItems[deal.id]?.items.map(item => (
                    <div key={`${item.id}-${item.dealId}`} className="cart-item deal-product-item">
                      <div className="item-image">
                        <img src={item.image} alt={item.name} />
                      </div>
                      <div className="item-details">
                        <div className="item-header">
                          <h3 className="item-name">{item.name}</h3>
                          <div className="price-display">
                            <span className="original-price">${item.originalPrice?.toFixed(2)}</span>
                            <span className="deal-price">${item.dealPrice?.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="item-meta">
                          <p className="item-category">{item.category}</p>
                          {item.weight && (
                            <div className="weight-info">
                              <FaWeightHanging className="weight-icon" />
                              <span className="weight-label">{getWeightLabel(item.weight)}</span>
                            </div>
                          )}
                        </div>
                        <div className="deal-item-badge">Deal Item</div>
                        <div className="item-controls">
                          <div className="quantity-controls">
                            <span className="quantity-display">{item.quantity}</span>
                          </div>
                          <button 
                            onClick={() => removeFromCart(item.id)}
                            className="remove-btn"
                          >
                            <FaTrash />
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>

          {/* Product selection modal */}
          {selectedDeal && dealRequirements[selectedDeal] && (
            <div className="deal-products-modal">
              <div className="modal-overlay" onClick={cancelSelection}></div>
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Select Products</h3>
                  <button onClick={cancelSelection} className="modal-close">
                    ×
                  </button>
                </div>
                
                <div className="modal-body">
                  <p className="modal-subtitle">
                    {dealRequirements[selectedDeal].description}
                  </p>
                  <p className="selection-status">
                    {selectedProducts.length > 0 
                      ? `Selected ${selectedProducts.length}/${dealRequirements[selectedDeal].productsNeeded} products`
                      : `Choose from ${dealRequirements[selectedDeal].category}`}
                  </p>
                  
                  <div className="products-grid">
                    {(dealProducts[selectedDeal] || []).map(product => (
                      <div 
                        key={product.id} 
                        className={`product-option ${
                          selectedProducts.some(p => p.id === product.id) ? 'selected' : ''
                        } ${
                          selectedProducts.length >= dealRequirements[selectedDeal].productsNeeded && 
                          !selectedProducts.some(p => p.id === product.id) ? 'disabled' : ''
                        }`}
                        onClick={(e) => handleProductSelect(selectedDeal, product, e)}
                      >
                        <img src={product.image} alt={product.name} className="product-image" />
                        <div className="product-info">
                          <p className="product-name">{product.name}</p>
                          <p className="product-price">${product.price.toFixed(2)}</p>
                          {selectedProducts.some(p => p.id === product.id) && (
                            <div className="selected-badge">Selected</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    onClick={cancelSelection}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => completeDealSelection(selectedDeal)}
                    className={`confirm-btn ${
                      selectedProducts.length !== dealRequirements[selectedDeal].productsNeeded ? 'disabled' : ''
                    }`}
                    disabled={selectedProducts.length !== dealRequirements[selectedDeal].productsNeeded}
                  >
                    {selectedProducts.length === dealRequirements[selectedDeal].productsNeeded 
                      ? 'Confirm Selection' 
                      : `Select ${dealRequirements[selectedDeal].productsNeeded - selectedProducts.length} More`}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="cart-summary">
            <div className="summary-card">
              <h2 className="summary-title">Order Summary</h2>
              
              {/* Regular items */}
              {regularItems.length > 0 && (
                <div className="summary-section">
                  <h3>Regular Items</h3>
                  {regularItems.map(item => (
                    <div key={`regular-summary-${item.id}`} className="summary-item">
                      <div className="item-info">
                        <span className="item-name">{item.name}</span>
                        {item.weight && (
                          <span className="item-weight"> - {getWeightLabel(item.weight)}</span>
                        )}
                      </div>
                      <span className="item-total">
                        {item.quantity} × ${item.price.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Deal items grouped by deal */}
              {Object.entries(groupedDealItems).map(([dealId, dealGroup]) => (
                <div key={`deal-group-${dealId}`} className="summary-section">
                  <h3>{dealGroup.dealName}</h3>
                  {dealGroup.items.map(item => (
                    <div key={`deal-summary-${item.id}`} className="summary-item deal-item">
                      <div className="item-info">
                        <span className="item-name">{item.name}</span>
                        {item.weight && (
                          <span className="item-weight"> - {getWeightLabel(item.weight)}</span>
                        )}
                      </div>
                      <span className="item-total">
                        {item.quantity} × ${item.dealPrice.toFixed(2)}
                        <span className="original-price">${item.originalPrice?.toFixed(2)}</span>
                      </span>
                    </div>
                  ))}
                  <div className="deal-subtotal">
                    <span>Deal Subtotal:</span>
                    <span>${dealGroup.total.toFixed(2)}</span>
                  </div>
                  <div className="deal-savings">
                    <span>Savings:</span>
                    <span>-${dealGroup.savings.toFixed(2)}</span>
                  </div>
                </div>
              ))}

              {/* Totals */}
              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${originalTotal.toFixed(2)}</span>
                </div>
                {totalSavings > 0 && (
                  <div className="summary-row savings">
                    <span>Total Savings</span>
                    <span>-${totalSavings.toFixed(2)}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>TOTAL</span>
                  <span>${displayTotal.toFixed(2)}</span>
                </div>
              </div>

              <Link to="/checkout" className="checkout-btn">
                Proceed to Checkout
              </Link>

              <Link to="/products" className="continue-shopping">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;